import React, { useCallback, useEffect, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { TaskyButton, TaskyEmptyState, TaskyErrorState, TaskyLightbox, TaskySpinner } from '../../tasky';
import { useCachedImageUrl } from '../../../hooks/useCachedImageUrl';
import { AdminContentRow, AdminContentType, PaginatedResult } from '../../../services/api/adminUserDetailService';

const TYPE_TABS: { key: AdminContentType; label: string }[] = [
  { key: 'banner', label: 'バナー' },
  { key: 'image', label: '画像' },
  { key: 'carousel', label: 'カルーセル' },
  { key: 'video', label: '動画' },
];

export interface GeneratedContentPanelProps {
  fetchPage: (type: AdminContentType, page: number) => Promise<PaginatedResult<AdminContentRow>>;
}

interface ContentThumbnailProps {
  item: AdminContentRow;
  onOpen: () => void;
}

// サムネイル1枚分の表示。画像系は useCachedImageUrl 経由でキャッシュし、失敗時はプレースホルダにフォールバックする
const ContentThumbnail = React.memo<ContentThumbnailProps>(({ item, onOpen }) => {
  const [hasError, setHasError] = useState(false);
  const thumbSrc = item.thumbnail_url || item.url;
  const { cachedUrl, onImageError } = useCachedImageUrl(item.id, thumbSrc);

  if (item.type === 'video') {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        src={item.url}
        controls
        preload="metadata"
        className="h-40 w-full rounded-t-tasky-md bg-black object-cover"
      />
    );
  }

  return (
    <button type="button" onClick={onOpen} className="block w-full">
      {hasError ? (
        <div className="flex h-40 w-full items-center justify-center rounded-t-tasky-md bg-warm-surface text-gray-300">
          <ImageIcon className="h-8 w-8" />
        </div>
      ) : (
        <img
          src={cachedUrl}
          alt={item.title || ''}
          loading="lazy"
          className="h-40 w-full rounded-t-tasky-md object-cover"
          onError={() => {
            onImageError();
            setHasError(true);
          }}
        />
      )}
    </button>
  );
});
ContentThumbnail.displayName = 'ContentThumbnail';

const GeneratedContentPanel = React.memo<GeneratedContentPanelProps>(({ fetchPage }) => {
  const [activeType, setActiveType] = useState<AdminContentType>('banner');
  const [items, setItems] = useState<AdminContentRow[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxItem, setLightboxItem] = useState<AdminContentRow | null>(null);

  const load = useCallback(async (type: AdminContentType, targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPage(type, targetPage);
      setItems(result.data);
      setPage(result.current_page);
      setLastPage(result.last_page);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to fetch generated contents:', err);
      setError('生成物の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => {
    load(activeType, 1);
  }, [load, activeType]);

  return (
    <div>
      <div className="mb-4 flex overflow-x-auto border-b border-warm-border">
        {TYPE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveType(tab.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeType === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:border-warm-border hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
          <TaskySpinner size="md" />
          読み込み中...
        </div>
      ) : error ? (
        <TaskyErrorState description={error} />
      ) : items.length === 0 ? (
        <TaskyEmptyState title="生成物がありません" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((item) => (
              <div
                key={`${item.type}-${item.id}`}
                className="overflow-hidden rounded-tasky-md border border-warm-border bg-white"
              >
                <ContentThumbnail item={item} onOpen={() => setLightboxItem(item)} />
                <div className="p-3">
                  <p className="truncate text-sm text-dark" title={item.title || undefined}>
                    {item.title || '(無題)'}
                  </p>
                  <p className="mt-1 truncate text-xs text-gray-500">{item.user?.name || '-'}</p>
                  <p className="mt-1 text-xs text-gray-400">{new Date(item.created_at).toLocaleString('ja-JP')}</p>
                </div>
              </div>
            ))}
          </div>

          {lastPage > 1 && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <TaskyButton
                variant="outline"
                size="sm"
                onClick={() => load(activeType, page - 1)}
                disabled={page <= 1 || loading}
              >
                前へ
              </TaskyButton>
              <span className="text-sm text-gray-500">{page} / {lastPage}（全{total}件）</span>
              <TaskyButton
                variant="outline"
                size="sm"
                onClick={() => load(activeType, page + 1)}
                disabled={page >= lastPage || loading}
              >
                次へ
              </TaskyButton>
            </div>
          )}
        </>
      )}

      <TaskyLightbox
        open={!!lightboxItem}
        onClose={() => setLightboxItem(null)}
        src={lightboxItem?.url}
        alt={lightboxItem?.title || ''}
      />
    </div>
  );
});
GeneratedContentPanel.displayName = 'GeneratedContentPanel';

export default GeneratedContentPanel;
